import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, gr } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Phone, EnvelopeSimple } from "@phosphor-icons/react";
import { TransientStays } from "@/components/TransientStays";

const STATUS = {
  new: { label: "Νέο", cls: "bg-[#DBEAFE] text-[#1E40AF]" },
  accepted: { label: "Αποδεκτό", cls: "bg-[#DCFCE7] text-[#065F46]" },
  rejected: { label: "Μη αποδεκτό", cls: "bg-[#FEE2E2] text-[#991B1B]" },
};

export default function Inquiries() {
  const qc = useQueryClient();
  const [active, setActive] = useState(null);
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["inquiries"],
    queryFn: async () => (await api.get("/inquiries")).data,
    refetchInterval: 20000,
  });

  const { data: config } = useQuery({ queryKey: ["config"], queryFn: async () => (await api.get("/config")).data });
  const { data: slots = [] } = useQuery({ queryKey: ["slots"], queryFn: async () => (await api.get("/slots")).data });

  const save = async () => {
    try {
      await api.put(`/inquiries/${active.id}`, { status: active.status, reply: active.reply || "" });
      await qc.invalidateQueries({ queryKey: ["inquiries"] });
      toast.success("Αποθηκεύτηκε");
      setActive(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα"); }
  };

  return (
    <div>
      <PageHeader title="Αιτήματα κρατήσεων" subtitle={`${items.length} αιτήματα κράτησης`} />
      <div className="space-y-6 p-6">
        {config?.facility_type === "sea" && <TransientStays slots={slots.filter((s) => !s.occupied)} />}
        <Card className="rounded-sm border-border shadow-none">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="inquiries-table">
              <thead>
                <tr className="border-b border-border text-left text-xs tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-semibold">Πελάτης</th>
                  <th className="px-4 py-3 font-semibold">Επικοινωνία</th>
                  <th className="px-4 py-3 font-semibold">Ζήτηση</th>
                  <th className="px-4 py-3 font-semibold">Κωδικός</th>
                  <th className="px-4 py-3 font-semibold">Κατάσταση</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">Φόρτωση...</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">Δεν υπάρχουν αιτήματα ακόμη.</td></tr>
                ) : items.map((it, i) => {
                  const st = STATUS[it.status] || STATUS.new;
                  return (
                    <tr key={it.id} className="border-b border-border last:border-0 hover:bg-secondary/50" data-testid={`inquiry-row-${i}`}>
                      <td className="px-4 py-3 font-medium">{it.visitor_name}</td>
                      <td className="px-4 py-3 text-xs">
                        {it.visitor_phone && <div className="flex items-center gap-1"><Phone size={12} /> {it.visitor_phone}</div>}
                        {it.visitor_email && <div className="flex items-center gap-1"><EnvelopeSimple size={12} /> {it.visitor_email}</div>}
                      </td>
                      <td className="px-4 py-3 text-xs">{it.length ? `${it.length}μ` : "—"} · {gr(it.start_date)} → {gr(it.end_date)}</td>
                      <td className="px-4 py-3 font-mono text-xs">{it.ref_code}</td>
                      <td className="px-4 py-3"><span className={`rounded-sm px-2 py-0.5 text-xs font-bold ${st.cls}`}>{st.label}</span></td>
                      <td className="px-4 py-3 text-right">
                        <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setActive({ ...it, reply: it.reply || "", status: it.status || "new" })} data-testid={`open-inquiry-${i}`}>Άνοιγμα</Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="rounded-sm sm:max-w-lg" data-testid="inquiry-detail-dialog">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="font-head">{active.visitor_name}</DialogTitle>
                <DialogDescription>Κωδικός {active.ref_code} · {gr((active.created_at || "").slice(0, 10))}</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="rounded-sm bg-secondary/60 p-3 text-sm">
                  {active.visitor_phone && <div>Τηλέφωνο: {active.visitor_phone}</div>}
                  {active.visitor_email && <div>Email: {active.visitor_email}</div>}
                  <div className="mt-1 text-xs text-muted-foreground">Ζήτηση: {active.length ? `${active.length}μ` : "—"} · {gr(active.start_date)} → {gr(active.end_date)}</div>
                </div>
                {active.message && <div className="rounded-sm border border-border p-3 text-sm">«{active.message}»</div>}
                <div>
                  <Label className="text-xs text-muted-foreground">Κατάσταση</Label>
                  <Select value={active.status} onValueChange={(v) => setActive({ ...active, status: v })}>
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="inquiry-status-select"><SelectValue placeholder="Επιλέξτε" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="accepted">Αποδεκτό (αποστολή email αποδοχής)</SelectItem>
                      <SelectItem value="rejected">Μη αποδεκτό</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Απάντηση προς τον πελάτη</Label>
                  <Textarea value={active.reply} onChange={(e) => setActive({ ...active, reply: e.target.value })} rows={3} className="mt-1 rounded-sm" data-testid="inquiry-reply-input" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-sm" onClick={() => setActive(null)}>Κλείσιμο</Button>
                <Button className="rounded-sm bg-[#0055FF] hover:bg-[#0044CC]" onClick={save} data-testid="inquiry-save">Αποθήκευση</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
